import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { USER_ROLE } from '../constants';
import { SalesforceContext } from '../context';
import { useAuthContext } from '../hooks';

export const SalesforceProvider = ({ children }) => {
  const { userRole } = useAuthContext();

  const [isSalesforceConnected, setIsSalesforceConnected] = useState(false);

  const [isSalesforceLoading, setIsSalesforceLoading] = useState(true);
  const [isSalesforceError, setIsSalesforceError] = useState(false);
  const [isSalesforceDisconnecting, setIsSalesforceDisconnecting] =
    useState(false);
  const [isSalesforceConnecting, setIsSalesforceConnecting] = useState(false);
  const [isSendToSalesforceLoading, setIsSendToSalesforceLoading] =
    useState(false);

  const connectSalesforce = async () => {
    setIsSalesforceConnecting(true);

    try {
      const response = await fetch('/api/v1/oauth/salesforce/authorize');

      if (!response.ok) throw new Error();

      const data = await response.json();
      setIsSalesforceConnecting(false);

      window.location.href = data.url;
    } catch {
      setIsSalesforceConnecting(false);
      toast.error('Failed to connect Salesforce.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const disconnectSalesforce = async () => {
    setIsSalesforceDisconnecting(true);

    try {
      const response = await fetch('/api/v1/integrations/salesforce', {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error();

      toast.info('Salesforce has been disconnected.', {
        position: 'top-right',
        autoClose: 3000,
      });

      setIsSalesforceConnected(false);
      setIsSalesforceDisconnecting(false);
    } catch {
      setIsSalesforceDisconnecting(false);
      toast.error('Failed to disconnect Salesforce.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const sendToSalesforce = async ({ body }) => {
    setIsSendToSalesforceLoading(true);

    try {
      const response = await fetch('/api/v1/integrations/send-to-salesforce', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error();

      toast.success('Sent to Salesforce successfully.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsSendToSalesforceLoading(false);
    } catch {
      toast.error('Failed to send data to Salesforce.', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsSendToSalesforceLoading(false);
    }
  };

  const fetchSalesforce = useCallback(async () => {
    setIsSalesforceLoading(true);
    setIsSalesforceError(false);

    try {
      const response = await fetch('/api/v1/integrations');

      if (!response.ok) throw new Error();

      const data = await response.json();

      const hasSalesforceConnect = data.integrations.some(
        ({ platform, isActive }) => (platform = 'salesforce' && isActive),
      );

      toast.info(
        hasSalesforceConnect
          ? 'Salesforce is connected.'
          : 'Salesforce is not connected.',
        {
          position: 'top-right',
          autoClose: 3000,
        },
      );

      setIsSalesforceConnected(hasSalesforceConnect ? true : false);
      setIsSalesforceLoading(false);
    } catch {
      setIsSalesforceLoading(false);
      setIsSalesforceError(true);
      toast.error('Failed to check the Salesforce connection.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  }, []);

  const isCaller = userRole === USER_ROLE.CALLER;

  useEffect(() => {
    if (isCaller) return;

    fetchSalesforce();
  }, [isCaller, fetchSalesforce]);

  const isSalesforceAvailable = isSalesforceConnected && !isSalesforceLoading;

  return (
    <SalesforceContext.Provider
      value={{
        isSalesforceLoading,
        isSalesforceError,
        isSalesforceAvailable,
        isSendToSalesforceLoading,
        isSalesforceDisconnecting,
        isSalesforceConnecting,
        connectSalesforce,
        disconnectSalesforce,
        sendToSalesforce,
        refetchSalesforce: fetchSalesforce,
      }}
    >
      {children}
    </SalesforceContext.Provider>
  );
};
